import { useMemo, useState } from 'react';

import { useSgCowEvents } from '../contexts/cow';
import { fmtDate, val } from '../utils/features';
import SgTrend from './trend';
import SgValue from './value';

import '../styles/sg.css';

// Historial de UNA feature para la vaca (admin-sg.md · B3): tabla paginada con
// "Cargar más" (acumulación por offset vía useInfiniteQuery — el server state vive en
// TanStack Query). Los eventos ya vienen AGREGADOS de todas las fichas de la vaca,
// orden event_date DESC. Las columnas salen del registro de features (única fuente);
// 'event_date' se lee de la raíz del evento, el resto de payload.
// D-005: filtro de fechas Desde/Hasta (server-side, B3 `from`/`to` inclusivo) que
// re-consulta acotado, y mini-gráfica de tendencia si la feature declara `chart`.

// `field` es el nombre de un campo del payload, el pseudo-campo 'event_date' (raíz del evento)
// o una FUNCIÓN (payload) => valor, para columnas DERIVADAS — p.ej. los litros/día de leche,
// que son la suma de ordeños y no un campo del DBF (ver `milkLiters` en utils/features).
function cell(ev, field, cls) {
    const raw = typeof field === 'function'
        ? field(ev.payload)
        : (field === 'event_date' ? ev.event_date : ev.payload?.[field]);
    if (cls === 'date') return <span className="sg-hist__cell--mono">{fmtDate(raw)}</span>;
    if (cls === 'mono') return <span className="sg-hist__cell--mono">{val(raw)}</span>;
    return <SgValue value={raw} />;   // pinta badges/notas; un valor plano pasa por `val`
}

// Key estable de una columna: el encabezado (único dentro de una feature). El campo no sirve
// como key cuando es una función.
const colKey = (field, header) => header;

export default function SgHistoryTab({ cowId, feature }) {
    const [from, setFrom] = useState('');   // 'YYYY-MM-DD' de <input type="date"> (UI state)
    const [to, setTo]     = useState('');

    // B3 responde 400 si to < from: mientras el rango sea incoherente (estado transitorio
    // al ajustar los inputs) no se aplica y se avisa — sin quemar un request seguro a 400.
    const invalidRange = !!(from && to && to < from);
    const applied = invalidRange ? {} : { from: from || undefined, to: to || undefined };

    const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
        useSgCowEvents({ cowId, feature: feature.key, ...applied });

    const rows = useMemo(() => data?.pages?.flatMap((p) => p.items) ?? [], [data]);
    const total = data?.pages?.[0]?.pagination?.total ?? 0;
    const filtered = !!(from || to);

    const clearRange = () => { setFrom(''); setTo(''); };

    return (
        <div className="sg-hist">
            <div className="sg-hist__range">
                <label className="sg-hist__range-field">
                    <span>Desde</span>
                    <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="Desde" />
                </label>
                <label className="sg-hist__range-field">
                    <span>Hasta</span>
                    <input type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="Hasta" />
                </label>
                {filtered && <button type="button" className="sg-hist__range-clear" onClick={clearRange}>Limpiar</button>}
                {invalidRange && <span className="sg-hist__range-hint">"Hasta" debe ser mayor o igual a "Desde"</span>}
            </div>

            {error ? (
                <div className="sg-hist__error">
                    {(error.fields && Object.values(error.fields)[0]) || error.message || `No se pudo cargar el historial de ${feature.label.toLowerCase()}`}
                </div>
            ) : isLoading ? (
                <div className="sg-hist__state">Cargando…</div>
            ) : !rows.length ? (
                <div className="sg-hist__state">
                    {filtered ? `Sin ${feature.label.toLowerCase()} en el rango seleccionado.` : `Sin ${feature.label.toLowerCase()}.`}
                </div>
            ) : (
                <>
                    {feature.chart && rows.length >= 2 && <SgTrend rows={rows} chart={feature.chart} />}

                    {/* La tabla scrollea DENTRO de su caja: con muchas columnas (leche, tratamientos)
                        la última quedaba recortada por el borde del panel. El scroll horizontal vive
                        acá y no en la página, que nunca debe desplazarse en horizontal. */}
                    <div className="sg-hist__scroll">
                        <table className="sg-hist__table">
                            <thead>
                                <tr>{feature.columns.map(([f, h]) => <th key={colKey(f, h)}>{h}</th>)}</tr>
                            </thead>
                            <tbody>
                                {rows.map((ev) => (
                                    <tr key={ev.id} className="sg-hist__row">
                                        {feature.columns.map(([f, h, cls]) => <td key={colKey(f, h)}>{cell(ev, f, cls)}</td>)}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="sg-hist__foot">
                        <span className="sg-hist__count">Mostrando {rows.length} de {total}</span>
                        {hasNextPage && (
                            <button type="button" className="sg-hist__more" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                                {isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
